import { showUserAlert } from "../utils/userErrorMessage";
import React, { useEffect, useMemo, useState } from "react";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Link } from "react-router-dom";
import { syncInstallmentExpensesForRows } from "../services/carInstallmentExpenseSync";

const getCurrentMonth = () => new Date().toISOString().slice(0, 7);
const getTodayLocal = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};
const toMonthDate = (month) => `${month || getCurrentMonth()}-01`;

const moneyFields = new Set([
  "balance_before_payment",
  "first_payment_amount",
  "first_payment_interest",
  "stamp_duty",
  "amount",
]);
const amountCalculationFields = new Set([
  "current_round",
  "maturity_round",
  "interest_rate",
  "loan_start_date",
  "maturity_date",
  "balance_before_payment",
  "first_payment_amount",
  "first_payment_interest",
  "stamp_duty",
]);

const toNumberOrNull = (value) => {
  const cleaned = String(value ?? "").replace(/[^\d.-]/g, "");
  if (!cleaned || cleaned === "-") return null;
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : null;
};

const truncateNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.trunc(number) : 0;
};

const truncateNumberOrNull = (value) => {
  const number = toNumberOrNull(value);
  return number === null ? null : Math.trunc(number);
};

const scheduleOverrideFields = ["payment", "principal", "interest", "stampDuty", "balance"];

const normalizeScheduleOverrides = (value) => {
  if (!value || typeof value !== "object") return {};
  if (Array.isArray(value)) {
    return value.reduce((acc, item) => {
      const round = Number(item?.round);
      if (!Number.isFinite(round)) return acc;
      acc[round] = scheduleOverrideFields.reduce((fieldAcc, field) => {
        if (item[field] !== undefined && item[field] !== null && String(item[field]).trim() !== "") {
          fieldAcc[field] = item[field];
        }
        return fieldAcc;
      }, {});
      return acc;
    }, {});
  }

  return Object.entries(value).reduce((acc, [roundKey, fields]) => {
    const round = Number(roundKey);
    if (!Number.isFinite(round) || !fields || typeof fields !== "object") return acc;
    acc[round] = scheduleOverrideFields.reduce((fieldAcc, field) => {
      if (fields[field] !== undefined && fields[field] !== null && String(fields[field]).trim() !== "") {
        fieldAcc[field] = fields[field];
      }
      return fieldAcc;
    }, {});
    return acc;
  }, {});
};

const getScheduleOverrideValue = (row, round, field) => {
  const overrides = normalizeScheduleOverrides(row.payment_schedule_overrides);
  const value = overrides?.[round]?.[field];
  return value === undefined || value === null || String(value).trim() === ""
    ? null
    : truncateNumberOrNull(value);
};

const formatMoneyInput = (value) => {
  const number = toNumberOrNull(value);
  if (number === null) return "";
  return Math.trunc(number).toLocaleString();
};

const buildEqualPayment = (principalAmount, monthlyRate, months) => {
  if (months <= 0 || principalAmount <= 0) return null;
  if (monthlyRate === 0) return principalAmount / months;
  return (principalAmount * monthlyRate * (1 + monthlyRate) ** months) /
    ((1 + monthlyRate) ** months - 1);
};

const getRemainingBalanceForFixedPayment = (payment, monthlyRate, months) => {
  if (payment <= 0 || months <= 0) return null;
  if (monthlyRate === 0) return payment * months;
  return (payment * ((1 + monthlyRate) ** months - 1)) /
    (monthlyRate * (1 + monthlyRate) ** months);
};

const getRepaymentDay = (maturityDate) => {
  if (!maturityDate || !/^\d{4}-\d{2}-\d{2}$/.test(maturityDate)) return "";
  return String(Number(maturityDate.slice(-2)));
};

const getCarNumberKey = (value = "") => String(value || "").replace(/\s+/g, "").trim();
const getCarNumberOnlyKey = (value = "") => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length >= 4 ? digits.slice(-4) : getCarNumberKey(value);
};
const getExistingCarKeys = (row = {}) =>
  [
    row.car_id ? `id:${row.car_id}` : "",
    row.car_number ? `num:${getCarNumberKey(row.car_number)}` : "",
    row.car_number ? `last4:${getCarNumberOnlyKey(row.car_number)}` : "",
    row.car_number_only ? `num:${getCarNumberKey(row.car_number_only)}` : "",
    row.car_number_only ? `last4:${getCarNumberOnlyKey(row.car_number_only)}` : "",
  ].filter(Boolean);
const getCarLookupKeys = (car = {}) => getExistingCarKeys({ ...car, car_id: car.id || car.car_id });
const buildCarLookup = (cars = []) => {
  const map = new Map();
  cars.forEach((car) => {
    getCarLookupKeys(car).forEach((key) => {
      if (!map.has(key)) map.set(key, car);
    });
  });
  return map;
};
const findCarInLookup = (row = {}, carLookup = new Map()) => {
  for (const key of getExistingCarKeys(row)) {
    const car = carLookup.get(key);
    if (car) return car;
  }
  return null;
};
const getClientRowKey = (row) => row.client_row_id || `${row.id || row.source_id || "new"}:${row.car_id || ""}:${row.car_number || ""}:${row.finance_company || ""}`;
const getEditableRowKey = (row) =>
  getClientRowKey(row);

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const openPrintWindow = (title, bodyHtml) => {
  const printWindow = window.open("", "_blank", "width=1200,height=800");
  if (!printWindow) {
    showUserAlert("팝업이 차단되어 인쇄 화면을 열 수 없습니다.");
    return;
  }

  printWindow.document.write(`
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(title)}</title>
        <style>
          @page { size: landscape; margin: 10mm; }
          body { font-family: Arial, "Malgun Gothic", sans-serif; color: #111827; }
          h1 { font-size: 18px; margin: 0 0 10px; }
          .meta { font-size: 12px; margin-bottom: 8px; color: #4b5563; }
          table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 11px; }
          th, td { border: 1px solid #9ca3af; padding: 4px 5px; vertical-align: middle; }
          th { background: #e5efff; text-align: center; font-weight: 700; }
          td.center { text-align: center; }
          td.right { text-align: right; }
          td.left { text-align: left; }
        </style>
      </head>
      <body>${bodyHtml}</body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => printWindow.print(), 250);
};

const getMonthIndex = (dateOrMonth) => {
  const text = String(dateOrMonth || "");
  const match = text.match(/^(\d{4})-(\d{2})/);
  if (!match) return null;
  return Number(match[1]) * 12 + Number(match[2]) - 1;
};

const toUtcDate = (dateText) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateText || ""))) return null;
  const [year, month, day] = dateText.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const formatDate = (date) => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getLastDayOfMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();

const resolveFirstRepaymentDate = (loanStartDate, maturityDate) => {
  const startDate = toUtcDate(loanStartDate);
  if (!startDate || !/^\d{4}-\d{2}-\d{2}$/.test(String(maturityDate || ""))) return null;

  const repaymentDay = Number(String(maturityDate).slice(-2));
  if (!Number.isFinite(repaymentDay) || repaymentDay <= 0) return null;

  let year = startDate.getUTCFullYear();
  let month = startDate.getUTCMonth() + 1;
  let day = Math.min(repaymentDay, getLastDayOfMonth(year, month));
  let candidate = new Date(Date.UTC(year, month - 1, day));

  if (candidate < startDate) {
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
    day = Math.min(repaymentDay, getLastDayOfMonth(year, month));
    candidate = new Date(Date.UTC(year, month - 1, day));
  }

  return formatDate(candidate);
};

const getInclusiveDayCount = (fromDate, toDate) => {
  const from = toUtcDate(fromDate);
  const to = toUtcDate(toDate);
  if (!from || !to || to < from) return null;
  return Math.floor((to.getTime() - from.getTime()) / 86400000);
};

const calculateFirstPaymentInterest = (row) => {
  const balanceBeforePayment = toNumberOrNull(row.balance_before_payment);
  const annualRate = toNumberOrNull(row.interest_rate);
  const firstRepaymentDate = resolveFirstRepaymentDate(row.loan_start_date, row.maturity_date);
  const dayCount = getInclusiveDayCount(row.loan_start_date, firstRepaymentDate);

  if (balanceBeforePayment === null || balanceBeforePayment <= 0) return null;
  if (annualRate === null) return null;
  if (!dayCount) return null;

  const monthlyInterest = balanceBeforePayment * (annualRate / 100 / 12);
  return truncateNumber(monthlyInterest * (dayCount / 30));
};

const applyCalculatedFirstPaymentInterest = (row, { force = false } = {}) => {
  if (row.first_payment_interest_manually_edited) return row;

  const firstInterestText = String(row.first_payment_interest ?? "").trim();
  if (firstInterestText && !row.first_payment_interest_auto_calculated && !force) return row;

  const calculatedInterest = calculateFirstPaymentInterest(row);
  if (calculatedInterest === null) return row;

  return {
    ...row,
    first_payment_interest: calculatedInterest,
    first_payment_interest_auto_calculated: true,
  };
};

const getRemainingInstallmentMonths = (row, month) => {
  const currentRound = Math.floor(toNumberOrNull(row.current_round) || 0);
  const maturityRound = Math.floor(toNumberOrNull(row.maturity_round) || 0);
  const roundBasedMonths =
    maturityRound > 0 ? (currentRound > 0 ? maturityRound - currentRound + 1 : maturityRound) : null;

  if (roundBasedMonths && roundBasedMonths > 0) return roundBasedMonths;

  const targetMonthIndex = getMonthIndex(toMonthDate(month));
  const maturityMonthIndex = getMonthIndex(row.maturity_date);
  if (targetMonthIndex === null || maturityMonthIndex === null) return null;

  const dateBasedMonths = maturityMonthIndex - targetMonthIndex + 1;
  return dateBasedMonths > 0 ? dateBasedMonths : null;
};

const getFirstPaymentPrincipal = (row) => {
  const firstPaymentAmount = toNumberOrNull(row.first_payment_amount);
  const firstPaymentInterest = toNumberOrNull(row.first_payment_interest);
  const stampDuty = toNumberOrNull(row.stamp_duty);
  if (
    firstPaymentAmount === null ||
    firstPaymentAmount <= 0 ||
    firstPaymentInterest === null ||
    stampDuty === null
  ) {
    return null;
  }

  return truncateNumber(Math.max(0, firstPaymentAmount - firstPaymentInterest - stampDuty));
};

const calculateInstallmentAmount = (row, month) => {
  const balanceBeforePayment = toNumberOrNull(row.balance_before_payment);
  const annualRate = toNumberOrNull(row.interest_rate);
  const remainingMonths = getRemainingInstallmentMonths(row, month);

  if (balanceBeforePayment === null || balanceBeforePayment <= 0) return null;
  if (annualRate === null) return null;
  if (!remainingMonths) return null;

  const monthlyRate = annualRate / 100 / 12;
  const firstPaymentPrincipal = getFirstPaymentPrincipal(row);
  let amount = null;

  if (firstPaymentPrincipal !== null && remainingMonths > 1) {
    amount = buildEqualPayment(
      Math.max(0, balanceBeforePayment - firstPaymentPrincipal),
      monthlyRate,
      remainingMonths - 1
    );
  } else {
    amount = buildEqualPayment(balanceBeforePayment, monthlyRate, remainingMonths);
  }

  if (!Number.isFinite(amount)) return null;
  return truncateNumber(amount);
};

const applyCalculatedAmount = (row, month) => {
  if (row.amount_manually_edited) return row;

  const amountText = String(row.amount ?? "").trim();
  if (amountText && !row.amount_auto_calculated) return row;

  const calculatedAmount = calculateInstallmentAmount(row, month);
  if (calculatedAmount === null) return row;

  return {
    ...row,
    amount: calculatedAmount,
    amount_auto_calculated: true,
  };
};

const applyInstallmentCalculations = (row, month, options = {}) =>
  applyCalculatedAmount(applyCalculatedFirstPaymentInterest(row, options), month);

const emptyInstallmentForCar = (car, month) => ({
  id: null,
  car_id: car.id || null,
  installment_month: toMonthDate(month),
  car_number: car.car_number || car.car_number_only || "",
  vehicle_year: car.year || "",
  finance_company: "",
  current_round: "",
  maturity_round: "",
  interest_rate: "",
  loan_start_date: "",
  maturity_date: "",
  balance_before_payment: "",
  first_payment_amount: "",
  first_payment_interest: "",
  stamp_duty: "",
  amount: "",
});

const hasInstallmentData = (row) =>
  [
    "finance_company",
    "current_round",
    "maturity_round",
    "interest_rate",
    "loan_start_date",
    "maturity_date",
    "balance_before_payment",
    "first_payment_amount",
    "first_payment_interest",
    "stamp_duty",
    "amount",
  ].some((field) => String(row[field] ?? "").trim());

const makeZeroExpenseFields = () => ({
  johab: 0,
  unyoung: 0,
  gps: 0,
  halbu: 0,
  halbu_balance: 0,
  geubyeo: 0,
  "4bohum": 0,
  tonghaeng: 0,
  joocha: 0,
  sooksic: 0,
  jungbi: 0,
  jooyu: 0,
  bohum: 0,
  secha: 0,
  chooljang: 0,
  gwatae: 0,
  gita: 0,
});

const validateDateInput = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));

const getPayableAmount = (row) => {
  const currentRound = toNumberOrNull(row.current_round);
  const overridePayment = getScheduleOverrideValue(row, currentRound, "payment");
  if (overridePayment !== null) return overridePayment;

  const firstPaymentAmount = toNumberOrNull(row.first_payment_amount);
  if (currentRound === 1 && firstPaymentAmount !== null) return firstPaymentAmount;
  return toNumberOrNull(row.amount) || 0;
};

const getPaidTotalAmount = (row) => {
  const currentRound = Math.max(0, Math.floor(toNumberOrNull(row.current_round) || 0));
  const amount = toNumberOrNull(row.amount) || 0;
  const firstPaymentAmount = toNumberOrNull(row.first_payment_amount);
  if (currentRound <= 0) return 0;

  let total = 0;
  for (let round = 1; round <= currentRound; round += 1) {
    const overridePayment = getScheduleOverrideValue(row, round, "payment");
    if (overridePayment !== null) {
      total += overridePayment;
    } else if (round === 1 && firstPaymentAmount !== null) {
      total += firstPaymentAmount;
    } else {
      total += amount;
    }
  }
  return truncateNumber(total);
};

const getPrincipalPaidForRound = (row, balanceBeforePayment, round) => {
  const overrideBalance = getScheduleOverrideValue(row, round, "balance");
  if (overrideBalance !== null) {
    return truncateNumber(Math.max(0, Math.min(balanceBeforePayment, balanceBeforePayment - overrideBalance)));
  }

  const overridePrincipal = getScheduleOverrideValue(row, round, "principal");
  if (overridePrincipal !== null) {
    return truncateNumber(Math.max(0, Math.min(balanceBeforePayment, overridePrincipal)));
  }

  const annualRate = toNumberOrNull(row.interest_rate) || 0;
  const monthlyRate = annualRate / 100 / 12;
  const amount = getScheduleOverrideValue(row, round, "payment") ?? toNumberOrNull(row.amount) ?? 0;
  const firstPaymentAmount = toNumberOrNull(row.first_payment_amount);
  const isFirstRound = round === 1 && firstPaymentAmount !== null;
  const payment = getScheduleOverrideValue(row, round, "payment") ?? (isFirstRound ? firstPaymentAmount : amount);
  if (!payment || balanceBeforePayment <= 0) return 0;

  const configuredFirstInterest = toNumberOrNull(row.first_payment_interest);
  const stampDuty = isFirstRound ? toNumberOrNull(row.stamp_duty) : null;
  if (isFirstRound && configuredFirstInterest !== null && stampDuty !== null) {
      return truncateNumber(Math.max(
      0,
      Math.min(balanceBeforePayment, payment - configuredFirstInterest - stampDuty)
    ));
  }

  if (isFirstRound) {
    const maturityRound = Math.floor(toNumberOrNull(row.maturity_round) || 0);
    const remainingRounds = maturityRound - round;
    const targetBalance = getRemainingBalanceForFixedPayment(amount, monthlyRate, remainingRounds);
    if (targetBalance !== null) {
      return truncateNumber(Math.max(0, Math.min(balanceBeforePayment, balanceBeforePayment - targetBalance)));
    }
  }

  const interest = balanceBeforePayment * monthlyRate;
  return truncateNumber(Math.max(0, Math.min(balanceBeforePayment, payment - interest - (stampDuty || 0))));
};

const getProjectedBalanceBeforeMonth = (installment, monthOffset, baseRound) => {
  let balance = toNumberOrNull(installment.balance_before_payment);
  if (balance === null) return "";

  for (let offset = 0; offset < monthOffset; offset += 1) {
    const round = baseRound + offset;
    balance = truncateNumber(Math.max(0, balance - getPrincipalPaidForRound(installment, balance, round)));
  }

  return truncateNumber(balance);
};

const projectInstallmentToMonth = (installment, targetMonth) => {
  const baseMonthIndex = getMonthIndex(installment.installment_month);
  const targetMonthIndex = getMonthIndex(toMonthDate(targetMonth));
  const earlyRepaymentMonthIndex = getMonthIndex(installment.early_repaid_date);
  const baseRound = Math.floor(toNumberOrNull(installment.current_round) || 0);
  const maturityRound = Math.floor(toNumberOrNull(installment.maturity_round) || 0);
  if (baseMonthIndex === null || targetMonthIndex === null) {
    return null;
  }

  const isEarlyRepaymentMonth =
    earlyRepaymentMonthIndex !== null && targetMonthIndex === earlyRepaymentMonthIndex;
  if (earlyRepaymentMonthIndex !== null && targetMonthIndex > earlyRepaymentMonthIndex) {
    return null;
  }

  const buildEarlyRepaymentMonthRow = () => ({
    ...installment,
    source_id: installment.id,
    source_installment_month: installment.installment_month,
    id: targetMonthIndex === baseMonthIndex ? installment.id : null,
    is_projected: targetMonthIndex !== baseMonthIndex,
    installment_month: toMonthDate(targetMonth),
    current_round: installment.maturity_round ?? "",
    maturity_round: installment.maturity_round ?? "",
    loan_start_date: installment.loan_start_date || "",
    maturity_date: installment.maturity_date || "",
    balance_before_payment: 0,
    first_payment_amount: installment.first_payment_amount ?? "",
    first_payment_interest: installment.first_payment_interest ?? "",
    stamp_duty: installment.stamp_duty ?? "",
    amount: 0,
  });

  if (isEarlyRepaymentMonth) {
    return buildEarlyRepaymentMonthRow();
  }

  if (baseRound <= 0 || maturityRound <= 0) {
    if (baseMonthIndex !== targetMonthIndex) return null;

    return {
      ...installment,
      source_id: installment.id,
      source_installment_month: installment.installment_month,
      id: installment.id,
      is_projected: false,
      installment_month: toMonthDate(targetMonth),
      current_round: installment.current_round ?? "",
      maturity_round: installment.maturity_round ?? "",
      loan_start_date: installment.loan_start_date || "",
      maturity_date: installment.maturity_date || "",
      balance_before_payment: installment.balance_before_payment ?? "",
      first_payment_amount: installment.first_payment_amount ?? "",
      first_payment_interest: installment.first_payment_interest ?? "",
      stamp_duty: installment.stamp_duty ?? "",
      amount: installment.amount ?? "",
    };
  }

  const monthOffset = targetMonthIndex - baseMonthIndex;
  const projectedRound = baseRound + monthOffset;
  if (projectedRound < baseRound || projectedRound > maturityRound) return null;

  const projectedBalanceBeforePayment = getProjectedBalanceBeforeMonth(
    installment,
    monthOffset,
    baseRound
  );
  const projectedPayment = getScheduleOverrideValue(installment, projectedRound, "payment");

  return {
    ...installment,
    source_id: installment.id,
    source_installment_month: installment.installment_month,
    id: targetMonthIndex === baseMonthIndex ? installment.id : null,
    is_projected: targetMonthIndex !== baseMonthIndex,
    installment_month: toMonthDate(targetMonth),
    current_round: projectedRound,
    loan_start_date: installment.loan_start_date || "",
    maturity_date: installment.maturity_date || "",
    balance_before_payment: projectedBalanceBeforePayment,
    first_payment_amount: installment.first_payment_amount ?? "",
    first_payment_interest: installment.first_payment_interest ?? "",
    stamp_duty: installment.stamp_duty ?? "",
    amount: projectedPayment ?? installment.amount ?? "",
  };
};

export default function CarInstallments() {
  const [month, setMonth] = useState(getCurrentMonth());
  const [filters, setFilters] = useState({ car_number: "", finance_company: "" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [earlyRepaying, setEarlyRepaying] = useState(false);
  const [lockedOrder, setLockedOrder] = useState(null);
  const [dirtyRowKeys, setDirtyRowKeys] = useState(new Set());
  const [selectedRowKeys, setSelectedRowKeys] = useState(new Set());

  const fetchData = async () => {
    const company = localStorage.getItem("email") || "";
    if (!company) {
      showUserAlert("회사 식별 정보가 없습니다. 다시 로그인해 주세요.");
      return;
    }

    setLoading(true);
    setLockedOrder(null);
    setDirtyRowKeys(new Set());
    setSelectedRowKeys(new Set());
    try {
      const { data: installments, error: installmentsError } = await supabase
        .from("car_installments")
        .select("*")
        .eq("company", company)
        .range(0, 1999);

      if (installmentsError) throw installmentsError;

      const { data: cars, error: carsError } = await supabase
        .from("cars")
        .select("id, car_number, car_number_only, year")
        .eq("company", company)
        .order("car_number", { ascending: true })
        .range(0, 1999);
      if (carsError) throw carsError;

      const carRows = cars || [];
      const carLookup = buildCarLookup(carRows);

      const projectedInstallments = (installments || [])
        .map((row) => projectInstallmentToMonth(row, month))
        .filter(Boolean);
      const installmentRows = projectedInstallments.map((installment) => {
        const car = findCarInLookup(installment, carLookup);
        if (!car) return null;
        const idPart = installment.id ? `id:${installment.id}` : `projection:${installment.source_id}:${toMonthDate(month)}`;
        return applyInstallmentCalculations({
          ...emptyInstallmentForCar(car, month),
          ...installment,
          client_row_id: idPart,
          car_id: car.id || installment.car_id || null,
          car_number: car.car_number || installment.car_number || "",
          vehicle_year: installment.vehicle_year || car.year || "",
          loan_start_date: installment.loan_start_date || "",
          balance_before_payment: installment.balance_before_payment ?? "",
          first_payment_amount: installment.first_payment_amount ?? "",
          first_payment_interest: installment.first_payment_interest ?? "",
          stamp_duty: installment.stamp_duty ?? "",
          amount: installment.amount ?? "",
        }, month);
      }).filter(Boolean);

      const installmentCarKeys = new Set(installmentRows.flatMap(getExistingCarKeys));
      const blankRows = carRows
        .filter((car) => !getCarLookupKeys(car).some((key) => installmentCarKeys.has(key)))
        .map((car) => ({
          ...emptyInstallmentForCar(car, month),
          client_row_id: `new:${car.id || car.car_number}`,
          should_save_empty: true,
        }));

      setRows([...installmentRows, ...blankRows]);
    } catch (error) {
      console.error("할부 내역 조회 실패:", error);
      showUserAlert("할부 내역 조회 실패: " + (error.message || JSON.stringify(error)));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayedRows = useMemo(() => {
    const carKeyword = filters.car_number.trim();
    const financeKeyword = filters.finance_company.trim();
    return rows
      .filter((row) => {
        if (carKeyword && !String(row.car_number || "").includes(carKeyword)) return false;
        if (
          financeKeyword &&
          !String(row.finance_company || "").toLowerCase().includes(financeKeyword.toLowerCase())
        ) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (lockedOrder) {
          const aOrder = lockedOrder[getClientRowKey(a)] ?? Number.MAX_SAFE_INTEGER;
          const bOrder = lockedOrder[getClientRowKey(b)] ?? Number.MAX_SAFE_INTEGER;
          if (aOrder !== bOrder) return aOrder - bOrder;
          return rows.indexOf(a) - rows.indexOf(b);
        }

        const aDay = Number(getRepaymentDay(a.maturity_date));
        const bDay = Number(getRepaymentDay(b.maturity_date));
        const aHasDay = Number.isFinite(aDay) && aDay > 0;
        const bHasDay = Number.isFinite(bDay) && bDay > 0;
        if (aHasDay && bHasDay && aDay !== bDay) return aDay - bDay;
        if (aHasDay !== bHasDay) return aHasDay ? -1 : 1;
        return String(a.car_number || "").localeCompare(String(b.car_number || ""), "ko-KR", {
          numeric: true,
        });
      });
  }, [filters.car_number, filters.finance_company, lockedOrder, rows]);

  const repaymentTotals = useMemo(() => {
    return displayedRows.reduce((acc, row) => {
      const day = getRepaymentDay(row.maturity_date);
      if (!day) return acc;
      acc[day] = (acc[day] || 0) + getPayableAmount(row);
      return acc;
    }, {});
  }, [displayedRows]);

  const tableTotals = useMemo(
    () =>
      displayedRows.reduce(
        (acc, row) => {
          const amount = toNumberOrNull(row.amount) || 0;
          const firstPaymentAmount = toNumberOrNull(row.first_payment_amount) || 0;
          const firstPaymentInterest = toNumberOrNull(row.first_payment_interest) || 0;
          const stampDuty = toNumberOrNull(row.stamp_duty) || 0;
          acc.balanceBeforePayment += toNumberOrNull(row.balance_before_payment) || 0;
          acc.firstPaymentAmount += firstPaymentAmount;
          acc.firstPaymentInterest += firstPaymentInterest;
          acc.stampDuty += stampDuty;
          acc.amount += amount;
          acc.groupTotal += getPayableAmount(row);
          acc.paidTotal += getPaidTotalAmount(row);
          return acc;
        },
        {
          balanceBeforePayment: 0,
          firstPaymentAmount: 0,
          firstPaymentInterest: 0,
          stampDuty: 0,
          amount: 0,
          groupTotal: 0,
          paidTotal: 0,
        }
      ),
    [displayedRows]
  );

  const repaymentSpans = useMemo(() => {
    const spans = {};
    displayedRows.forEach((row, index) => {
      const day = getRepaymentDay(row.maturity_date);
      if (!day) return;
      const previousDay = index > 0 ? getRepaymentDay(displayedRows[index - 1].maturity_date) : "";
      if (day === previousDay) return;

      let span = 1;
      for (let nextIndex = index + 1; nextIndex < displayedRows.length; nextIndex += 1) {
        if (getRepaymentDay(displayedRows[nextIndex].maturity_date) !== day) break;
        span += 1;
      }
      spans[index] = span;
    });
    return spans;
  }, [displayedRows]);

  const earlyRepaymentDisabled = saving || deleting || earlyRepaying || selectedRowKeys.size !== 1;

  const updateRow = (rowKey, field, value) => {
    const targetRow = rows.find((row) => getClientRowKey(row) === rowKey);
    if (targetRow) {
      setDirtyRowKeys((prev) => {
        const next = new Set(prev);
        next.add(getEditableRowKey(targetRow));
        return next;
      });
    }

    setRows((prev) =>
      prev.map((row) =>
        getClientRowKey(row) === rowKey
          ? (() => {
              const nextRow = {
                ...row,
                [field]: moneyFields.has(field) ? value.replace(/[^\d-]/g, "") : value,
              };

              if (field === "amount") {
                return {
                  ...nextRow,
                  amount_manually_edited: String(nextRow.amount ?? "").trim() !== "",
                  amount_auto_calculated: false,
                };
              }

              if (field === "first_payment_interest") {
                const withFirstInterestState = {
                  ...nextRow,
                  first_payment_interest_manually_edited:
                    String(nextRow.first_payment_interest ?? "").trim() !== "",
                  first_payment_interest_auto_calculated: false,
                };
                return withFirstInterestState.first_payment_interest_manually_edited
                  ? applyCalculatedAmount(withFirstInterestState, month)
                  : applyInstallmentCalculations(withFirstInterestState, month, { force: true });
              }

              if (amountCalculationFields.has(field)) {
                return applyInstallmentCalculations(nextRow, month, {
                  force: [
                    "interest_rate",
                    "loan_start_date",
                    "maturity_date",
                    "balance_before_payment",
                  ].includes(field),
                });
              }

              return nextRow;
            })()
          : row
      )
    );
  };

  const lockCurrentOrder = () => {
    if (lockedOrder) return;
    setLockedOrder(
      displayedRows.reduce((acc, row, index) => {
        acc[getClientRowKey(row)] = index;
        return acc;
      }, {})
    );
  };

  const buildPayload = (row) => ({
    company: localStorage.getItem("email") || "",
    installment_month: toMonthDate(month),
    car_id: row.car_id || null,
    car_number: row.car_number || "",
    vehicle_year: row.vehicle_year || null,
    finance_company: row.finance_company || null,
    current_round: toNumberOrNull(row.current_round),
    maturity_round: toNumberOrNull(row.maturity_round),
    interest_rate: row.interest_rate || null,
    loan_start_date: row.loan_start_date || null,
    maturity_date: row.maturity_date || null,
    balance_before_payment: truncateNumberOrNull(row.balance_before_payment),
    first_payment_amount: truncateNumberOrNull(row.first_payment_amount),
    first_payment_interest: truncateNumberOrNull(row.first_payment_interest),
    stamp_duty: truncateNumberOrNull(row.stamp_duty),
    amount: truncateNumberOrNull(row.amount),
  });

  const handleSave = async () => {
    const rowsToSave = rows.filter(
      (row) =>
        row.id ||
        row.should_save_empty ||
        (dirtyRowKeys.has(getEditableRowKey(row)) && hasInstallmentData(row))
    );
    if (rowsToSave.length === 0) {
      showUserAlert("저장할 할부 내역이 없습니다.");
      return;
    }

    setSaving(true);
    try {
      const company = localStorage.getItem("email") || "";
      const companyName = localStorage.getItem("companyName") || null;
      const savedInstallments = [];

      for (const row of rowsToSave) {
        const payload = buildPayload(row);
        const request = row.id
          ? supabase.from("car_installments").update(payload).eq("id", row.id).eq("company", company)
          : supabase.from("car_installments").insert(payload);
        const { data: savedRow, error } = await request.select("*").single();
        if (error) throw error;
        if (savedRow) savedInstallments.push(savedRow);
      }

      await syncInstallmentExpensesForRows({
        supabase,
        installments: savedInstallments,
        month,
        company,
        companyName,
      });

      showUserAlert("저장되었습니다.");
      await fetchData();
    } catch (error) {
      console.error("할부 내역 저장 실패:", error);
      showUserAlert("저장 실패: " + (error.message || JSON.stringify(error)));
    } finally {
      setSaving(false);
    }
  };

  const toggleSelectedRow = (rowKey) => {
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const selectableRows = displayedRows;
  const allDisplayedSelected =
    selectableRows.length > 0 && selectableRows.every((row) => selectedRowKeys.has(getClientRowKey(row)));

  const toggleAllDisplayedRows = () => {
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (allDisplayedSelected) {
        selectableRows.forEach((row) => next.delete(getClientRowKey(row)));
      } else {
        selectableRows.forEach((row) => next.add(getClientRowKey(row)));
      }
      return next;
    });
  };

  const deleteInstallmentRows = async (rowsToDelete, company) => {
    let deletedCount = 0;
    const persistedRows = rowsToDelete.filter((row) => row.id || row.source_id);

    for (const row of persistedRows) {
      const sourceId = row.source_id || row.id;
      const { data, error } = await supabase
        .from("car_installments")
        .delete()
        .eq("id", sourceId)
        .eq("company", company)
        .select("id");
      if (error) throw error;
      const rowDeletedCount = Array.isArray(data) ? data.length : 0;
      if (rowDeletedCount === 0) {
        throw new Error(`DB에서 삭제되지 않은 할부 내역이 있습니다. id=${sourceId}`);
      }
      deletedCount += rowDeletedCount;
    }

    if (persistedRows.length > 0 && deletedCount === 0) {
      throw new Error("DB에서 삭제된 할부 내역이 없습니다. RLS 삭제 정책 또는 삭제 조건을 확인해 주세요.");
    }

    return deletedCount;
  };

  const removeRowsFromScreen = (selectedKeys, rowsToDelete) => {
    setRows((prev) => prev.filter((row) => !selectedKeys.has(getClientRowKey(row))));
    setDirtyRowKeys((prev) => {
      const next = new Set(prev);
      rowsToDelete.forEach((row) => next.delete(getEditableRowKey(row)));
      return next;
    });
    setSelectedRowKeys(new Set());
  };

  const getSelectedRows = () => {
    const selectedKeys = new Set(selectedRowKeys);
    return {
      selectedKeys,
      selectedRows: rows.filter((row) => selectedKeys.has(getClientRowKey(row))),
    };
  };

  const handleDelete = async () => {
    const { selectedKeys, selectedRows: rowsToDelete } = getSelectedRows();
    if (rowsToDelete.length === 0) {
      showUserAlert("삭제할 할부 내역을 선택해 주세요.");
      return;
    }

    const ok = window.confirm(`선택한 할부 내역 ${rowsToDelete.length}건을 삭제하시겠습니까?`);
    if (!ok) return;

    setDeleting(true);
    try {
      const company = localStorage.getItem("email") || "";
      await deleteInstallmentRows(rowsToDelete, company);
      removeRowsFromScreen(selectedKeys, rowsToDelete);
      showUserAlert("삭제되었습니다.");
    } catch (error) {
      console.error("할부 내역 삭제 실패:", error);
      showUserAlert("삭제 실패: " + (error.message || JSON.stringify(error)));
    } finally {
      setDeleting(false);
    }
  };

  const handleEarlyRepayment = async () => {
    const { selectedKeys, selectedRows } = getSelectedRows();
    if (selectedRows.length === 0) {
      showUserAlert("조기 상환할 할부 내역을 선택해 주세요.");
      return;
    }
    if (selectedRows.length !== 1) {
      showUserAlert("조기 상환은 한 건씩만 처리할 수 있습니다.");
      return;
    }

    const unsavedRows = selectedRows.filter((row) => !(row.id || row.source_id));
    if (unsavedRows.length > 0) {
      showUserAlert("저장되지 않은 할부 내역은 조기 상환 처리할 수 없습니다.");
      return;
    }

    const invalidRows = selectedRows.filter((row) => (toNumberOrNull(row.balance_before_payment) || 0) <= 0);
    if (invalidRows.length > 0) {
      showUserAlert("결재전잔액이 0원 이하인 행은 조기 상환 처리할 수 없습니다.");
      return;
    }
    if (selectedRows.some((row) => row.early_repaid_date)) {
      showUserAlert("이미 조기 상환 처리된 할부 내역입니다.");
      return;
    }

    const repaymentDate = window.prompt("상환 날짜를 입력해 주세요. 예: 2026-09-22", getTodayLocal());
    if (repaymentDate === null) return;
    if (!validateDateInput(repaymentDate)) {
      showUserAlert("상환 날짜는 YYYY-MM-DD 형식으로 입력해 주세요.");
      return;
    }

    const totalAmount = selectedRows.reduce(
      (sum, row) => sum + (toNumberOrNull(row.balance_before_payment) || 0),
      0
    );
    const ok = window.confirm(
      `선택한 할부 내역 ${selectedRows.length}건을 ${repaymentDate}에 조기 상환 처리할까요?\n` +
      `지출 내역의 할부 잔액에 ${formatMoneyInput(totalAmount)}원이 등록되고, 다음달부터 해당 할부 내역은 조회되지 않습니다.`
    );
    if (!ok) return;

    setEarlyRepaying(true);
    try {
      const company = localStorage.getItem("email") || "";
      const companyName = localStorage.getItem("companyName") || null;
      const { data: cars, error: carsError } = await supabase
        .from("cars")
        .select("id, car_number, car_number_only, driver, phone, company_name")
        .eq("company", company)
        .range(0, 4999);
      if (carsError) throw carsError;

      const carLookup = buildCarLookup(cars || []);
      const expenseRows = selectedRows.map((row) => {
        const sourceId = row.source_id || row.id;
        const car = findCarInLookup(row, carLookup);
        const amount = truncateNumberOrNull(row.balance_before_payment) || 0;
        return {
          company,
          company_name: car?.company_name || companyName || null,
          date: repaymentDate,
          car_number: String(car?.car_number_only || row.car_number || "").trim(),
          human_name: car?.driver || "",
          phone: car?.phone || null,
          ...makeZeroExpenseFields(),
          halbu_balance: amount,
          bigo: `할부 조기 상환 #${sourceId}`,
        };
      });

      const { data: insertedExpenses, error: expenseError } = await supabase
        .from("cars-expenses")
        .insert(expenseRows)
        .select("id");
      if (expenseError) throw expenseError;

      const targetRow = selectedRows[0];
      const sourceId = targetRow.source_id || targetRow.id;
      const { data: updatedInstallment, error: updateError } = await supabase
        .from("car_installments")
        .update({
          early_repaid_date: repaymentDate,
          early_repaid_balance: truncateNumberOrNull(targetRow.balance_before_payment) || 0,
        })
        .eq("id", sourceId)
        .eq("company", company)
        .select("*")
        .single();

      if (updateError) {
        const expenseIds = (insertedExpenses || []).map((row) => row.id).filter(Boolean);
        if (expenseIds.length) {
          await supabase.from("cars-expenses").delete().eq("company", company).in("id", expenseIds);
        }
        throw updateError;
      }

      const updatedRow = {
        ...targetRow,
        ...updatedInstallment,
        current_round: targetRow.maturity_round,
        balance_before_payment: 0,
        amount: 0,
      };
      setRows((prev) =>
        prev.map((row) => (selectedKeys.has(getClientRowKey(row)) ? updatedRow : row))
      );
      setSelectedRowKeys(new Set());
      showUserAlert("조기 상환 처리되었습니다.");
    } catch (error) {
      console.error("할부 조기 상환 실패:", error);
      showUserAlert("조기 상환 실패: " + (error.message || JSON.stringify(error)));
    } finally {
      setEarlyRepaying(false);
    }
  };

  const handlePrint = () => {
    const selectedRows = displayedRows
      .map((row, index) => ({ row, displayIndex: index + 1 }))
      .filter(({ row }) => selectedRowKeys.has(getClientRowKey(row)));

    if (selectedRows.length === 0) {
      showUserAlert("인쇄할 할부 내역을 선택해 주세요.");
      return;
    }

    const filterText = [
      `연월: ${month}`,
      filters.car_number ? `차량번호: ${filters.car_number}` : "",
      filters.finance_company ? `할부사: ${filters.finance_company}` : "",
    ]
      .filter(Boolean)
      .join(" / ");

    const rowsHtml = selectedRows
      .map(({ row, displayIndex }) => {
        const repaymentDay = getRepaymentDay(row.maturity_date);
        return `
          <tr>
            <td class="center">${displayIndex}</td>
            <td class="center">${escapeHtml(repaymentDay)}</td>
            <td class="center">${escapeHtml(row.car_number)}</td>
            <td class="center">${escapeHtml(row.vehicle_year)}</td>
            <td class="left">${escapeHtml(row.finance_company)}</td>
            <td class="center">${escapeHtml(row.loan_start_date)}</td>
            <td class="center">${escapeHtml(row.current_round)}</td>
            <td class="center">${escapeHtml(row.maturity_round)}</td>
            <td class="center">${escapeHtml(row.interest_rate)}</td>
            <td class="center">${escapeHtml(row.maturity_date)}</td>
            <td class="right">${escapeHtml(formatMoneyInput(row.balance_before_payment))}</td>
            <td class="right">${escapeHtml(formatMoneyInput(row.first_payment_amount))}</td>
            <td class="right">${escapeHtml(formatMoneyInput(row.amount))}</td>
            <td class="right">${escapeHtml(formatMoneyInput(getPaidTotalAmount(row)))}</td>
            <td class="right">${escapeHtml(formatMoneyInput(repaymentTotals[repaymentDay] || 0))}</td>
          </tr>
        `;
      })
      .join("");

    openPrintWindow(
      "할부 내역",
      `
        <h1>할부 내역</h1>
        <div class="meta">${escapeHtml(filterText)} / 선택 ${selectedRows.length}건</div>
        <table>
          <thead>
            <tr>
              <th>순서</th>
              <th>상환<br />날짜</th>
              <th>차량번호</th>
              <th>연식</th>
              <th>할부사</th>
              <th>대출일자</th>
              <th>현재회차</th>
              <th>만기회차</th>
              <th>이율</th>
              <th>만기일</th>
              <th>결재전잔액</th>
              <th>첫달금액</th>
              <th>금액</th>
              <th>납입합계</th>
              <th>상환일 합계</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      `
    );
  };

  const handleLoadAllCars = async () => {
    const ok = window.confirm(
      "차량 데이터 전체를 가져오게 되면, 현재 없는(지우거나 새로 만들어진 차량 데이터)를 가져오게 됩니다. 진행하시겠습니까?"
    );
    if (!ok) return;

    const company = localStorage.getItem("email") || "";
    if (!company) {
      showUserAlert("회사 식별 정보가 없습니다. 다시 로그인해 주세요.");
      return;
    }

    setLoading(true);
    try {
      const { data: cars, error: carsError } = await supabase
        .from("cars")
        .select("id, car_number, car_number_only, year")
        .eq("company", company)
        .order("car_number", { ascending: true })
        .range(0, 1999);

      if (carsError) throw carsError;

      const existingCarKeys = new Set(rows.flatMap(getExistingCarKeys));
      const isExistingCar = (car) =>
        getExistingCarKeys({ ...car, car_id: car.id }).some((key) => existingCarKeys.has(key));

      const missingRows = (cars || [])
        .filter((car) => !isExistingCar(car))
        .map((car) => ({
          ...emptyInstallmentForCar(car, month),
          client_row_id: `new:${car.id || car.car_number}`,
          should_save_empty: true,
        }));

      if (missingRows.length === 0) {
        showUserAlert("새로 가져올 차량 데이터가 없습니다.");
        return;
      }

      setRows((prev) => [...prev, ...missingRows]);
      showUserAlert(`${missingRows.length}대의 차량 데이터를 추가했습니다. 현재 화면에는 총 ${rows.length + missingRows.length}대가 표시됩니다.`);
    } catch (error) {
      console.error("차량 데이터 전체 가져오기 실패:", error);
      showUserAlert("차량 데이터 전체 가져오기 실패: " + (error.message || JSON.stringify(error)));
    } finally {
      setLoading(false);
    }
  };

  const renderInput = (row, field, className = "w-full") => (
    <Input
      value={moneyFields.has(field) ? formatMoneyInput(row[field]) : row[field] || ""}
      onChange={(event) => updateRow(getClientRowKey(row), field, event.target.value)}
      onFocus={lockCurrentOrder}
      className={className}
      disabled={saving || deleting || earlyRepaying}
    />
  );

  return (
    <div className="space-y-1">
      <Header />
      <div className="px-4 pt-1 pb-4 space-y-2">
        <h2 className="text-lg font-semibold">할부 내역</h2>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">연월</span>
          <Input
            type="month"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            className="w-36"
          />
          <Input
            placeholder="차량번호"
            value={filters.car_number}
            onChange={(event) => setFilters((prev) => ({ ...prev, car_number: event.target.value }))}
            className="w-40"
          />
          <Input
            placeholder="할부사"
            value={filters.finance_company}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, finance_company: event.target.value }))
            }
            className="w-40"
          />
          <Button onClick={() => fetchData()} disabled={loading}>
            {loading ? "조회중..." : "검색"}
          </Button>
        </div>

        <Card className="border-0 shadow-none">
          <CardContent className="bg-white p-0">
            <div className="mb-2 flex items-center text-sm">
              <div className="text-gray-600">총 {displayedRows.length}대</div>
            </div>

            <div className="max-h-[72vh] overflow-auto">
              <table className="table-fixed w-full min-w-[1360px] border border-gray-300 text-xs whitespace-nowrap">
                <colgroup>
                  <col className="w-10" />
                  <col className="w-12" />
                  <col className="w-10" />
                  <col className="w-28" />
                  <col className="w-20" />
                  <col className="w-32" />
                  <col className="w-32" />
                  <col className="w-10" />
                  <col className="w-10" />
                  <col className="w-20" />
                  <col className="w-32" />
                  <col className="w-28" />
                  <col className="w-24" />
                  <col className="w-28" />
                  <col className="w-28" />
                  <col className="w-28" />
                </colgroup>
                <thead className="sticky top-0 z-20 bg-blue-500 text-white">
                  <tr>
                    <th className="border px-2 py-1 text-center" rowSpan={2}>
                      <input
                        type="checkbox"
                        checked={allDisplayedSelected}
                        onChange={toggleAllDisplayedRows}
                        disabled={selectableRows.length === 0 || saving || deleting || earlyRepaying}
                      />
                    </th>
                    <th className="border px-2 py-1 text-center" rowSpan={2}>순서</th>
                    <th className="border px-1 py-1 text-center leading-tight" rowSpan={2}>
                      <span className="block">상환</span>
                      <span className="block">날짜</span>
                    </th>
                    <th className="border px-2 py-1 text-center" rowSpan={2}>차량번호</th>
                    <th className="border px-2 py-1 text-center" rowSpan={2}>연식</th>
                    <th className="border px-2 py-1 text-center" rowSpan={2}>할부사</th>
                    <th className="border px-2 py-1 text-center" rowSpan={2}>대출일자</th>
                    <th className="border px-2 py-1 text-center" colSpan={2}>회차</th>
                    <th className="border px-2 py-1 text-center" rowSpan={2}>이율</th>
                    <th className="border px-2 py-1 text-center" rowSpan={2}>만기일</th>
                    <th className="border px-2 py-1 text-center" rowSpan={2}>결재전잔액</th>
                    <th className="border px-2 py-1 text-center" rowSpan={2}>첫달금액</th>
                    <th className="border px-2 py-1 text-center" rowSpan={2}>금액</th>
                    <th className="border px-2 py-1 text-center" rowSpan={2}>납입합계</th>
                    <th className="border px-2 py-1 text-center" rowSpan={2}>상환일 합계</th>
                  </tr>
                  <tr>
                    <th className="border px-2 py-1 text-center">현재</th>
                    <th className="border px-2 py-1 text-center">만기</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedRows.map((row, displayIndex) => {
                    const repaymentDay = getRepaymentDay(row.maturity_date);
                    const repaymentRowSpan = repaymentSpans[displayIndex] || 0;
                    const rowKey = getClientRowKey(row);
                    return (
                      <tr key={rowKey} className="hover:bg-gray-50">
                        <td className="border px-2 py-1 text-center">
                          <input
                            type="checkbox"
                            checked={selectedRowKeys.has(rowKey)}
                            onChange={() => toggleSelectedRow(rowKey)}
                            disabled={saving || deleting || earlyRepaying}
                          />
                        </td>
                        <td className="border px-2 py-1 text-center">{displayIndex + 1}</td>
                        {repaymentDay && repaymentRowSpan > 0 && (
                          <td className="border px-2 py-1 text-center align-middle" rowSpan={repaymentRowSpan}>
                            {repaymentDay}
                          </td>
                        )}
                        {!repaymentDay && <td className="border px-2 py-1 text-center"></td>}
                        <td className="border px-2 py-1 text-center">
                          <Link
                            to={`/car-installments/${encodeURIComponent(row.car_number || "")}`}
                            state={{
                              installmentId: row.id || null,
                              sourceId: row.source_id || null,
                              carNumber: row.car_number || "",
                              principal:
                                toNumberOrNull(row.balance_before_payment) ||
                                toNumberOrNull(row.amount) ||
                                "",
                              interestRate: row.interest_rate || "",
                              termMonths: row.maturity_round || "",
                              graceMonths: row.grace_months ?? 0,
                              loanStartDate: row.loan_start_date || "",
                              maturityDate: row.maturity_date || "",
                              firstPaymentAmount: row.first_payment_amount || "",
                              firstPaymentInterest: row.first_payment_interest || "",
                              stampDuty: row.stamp_duty || "",
                              paymentScheduleOverrides: row.payment_schedule_overrides || null,
                            }}
                            className="text-blue-600 underline hover:text-blue-800"
                          >
                            {row.car_number}
                          </Link>
                        </td>
                        <td className="border px-1 py-1 text-center">
                          {renderInput(row, "vehicle_year", "w-full text-center")}
                        </td>
                        <td className="border px-1 py-1 text-center">
                          {renderInput(row, "finance_company")}
                        </td>
                        <td className="border px-1 py-1 text-center">
                          <Input
                            type="date"
                            value={row.loan_start_date || ""}
                            onChange={(event) => updateRow(rowKey, "loan_start_date", event.target.value)}
                            onFocus={lockCurrentOrder}
                            className="w-full"
                            disabled={saving || deleting || earlyRepaying}
                          />
                        </td>
                        <td className="border px-1 py-1 text-center">
                          {renderInput(row, "current_round", "w-full text-center")}
                        </td>
                        <td className="border px-1 py-1 text-center">
                          {renderInput(row, "maturity_round", "w-full text-center")}
                        </td>
                        <td className="border px-1 py-1 text-center">
                          {renderInput(row, "interest_rate", "w-full text-center")}
                        </td>
                        <td className="border px-1 py-1 text-center">
                          <Input
                            type="date"
                            value={row.maturity_date || ""}
                            onChange={(event) => updateRow(rowKey, "maturity_date", event.target.value)}
                            onFocus={lockCurrentOrder}
                            className="w-full"
                            disabled={saving || deleting || earlyRepaying}
                          />
                        </td>
                        <td className="border px-1 py-1 text-right">
                          {renderInput(row, "balance_before_payment", "w-full text-right")}
                        </td>
                        <td className="border px-1 py-1 text-right">
                          {renderInput(row, "first_payment_amount", "w-full text-right")}
                        </td>
                        <td className="border px-1 py-1 text-right">
                          {renderInput(row, "amount", "w-full text-right")}
                        </td>
                        <td className="border px-1 py-1 text-right font-semibold">
                          {formatMoneyInput(getPaidTotalAmount(row))}
                        </td>
                        {repaymentDay && repaymentRowSpan > 0 && (
                          <td
                            className="border px-1 py-1 text-right font-semibold bg-gray-50 align-middle"
                            rowSpan={repaymentRowSpan}
                          >
                            {formatMoneyInput(repaymentTotals[repaymentDay])}
                          </td>
                        )}
                        {!repaymentDay && <td className="border px-1 py-1 text-right bg-gray-50"></td>}
                      </tr>
                    );
                  })}
                  {displayedRows.length > 0 && (
                    <tr className="bg-gray-100 font-semibold">
                      <td className="border px-2 py-1 text-center" colSpan={11}>
                        합계
                      </td>
                      <td className="border px-1 py-1 text-right">
                        {formatMoneyInput(tableTotals.balanceBeforePayment)}
                      </td>
                      <td className="border px-1 py-1 text-right">
                        {formatMoneyInput(tableTotals.firstPaymentAmount)}
                      </td>
                      <td className="border px-1 py-1 text-right">
                        {formatMoneyInput(tableTotals.amount)}
                      </td>
                      <td className="border px-1 py-1 text-right">
                        {formatMoneyInput(tableTotals.paidTotal)}
                      </td>
                      <td className="border px-1 py-1 text-right">
                        {formatMoneyInput(tableTotals.groupTotal)}
                      </td>
                    </tr>
                  )}
                  {!loading && displayedRows.length === 0 && (
                    <tr>
                      <td className="border px-2 py-6 text-center text-gray-500" colSpan={16}>
                        조회된 차량이 없습니다.
                      </td>
                    </tr>
                  )}
                  {loading && (
                    <tr>
                      <td className="border px-2 py-6 text-center text-gray-500" colSpan={16}>
                        불러오는 중...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex items-center justify-between gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleLoadAllCars}
                disabled={loading || saving || deleting || earlyRepaying}
              >
                차량 데이터 전체 가져오기
              </Button>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePrint}
                  disabled={selectedRowKeys.size === 0 || earlyRepaying}
                >
                  인쇄
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleEarlyRepayment}
                  disabled={earlyRepaymentDisabled}
                  className={earlyRepaymentDisabled ? "cursor-not-allowed bg-gray-300 text-gray-500 hover:bg-gray-300" : ""}
                >
                  {earlyRepaying ? "처리중..." : "조기 상환"}
                </Button>
                <Button
                  type="button"
                  onClick={handleDelete}
                  disabled={saving || deleting || earlyRepaying || selectedRowKeys.size === 0}
                  className="bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-300"
                >
                  {deleting ? "삭제중..." : "삭제"}
                </Button>
                <Button onClick={handleSave} disabled={saving || loading || deleting || earlyRepaying}>
                  {saving ? "저장중..." : "저장"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
